import React from 'react';
import {
  File,
  FileAudio,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Image as ImageIcon,
  LucideIcon,
  Presentation,
} from 'lucide-react';
import { MEDIA_TONE, MediaKind, Tone } from '../ui/colors';

/** Lucide icon for a MIME type (replaces the emoji icons from mediaService). */
export function mediaTypeIcon(mimeType: string = ''): LucideIcon {
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('video/')) return FileVideo;
  if (mimeType.startsWith('audio/')) return FileAudio;
  if (mimeType === 'application/pdf' || mimeType.includes('word') || mimeType.startsWith('text/')) return FileText;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return FileSpreadsheet;
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return Presentation;
  return File;
}

/** Media kind of a MIME type, for its colour (image, video, audio, PDF, document, other) */
export function mediaKindOf(mimeType: string = ''): MediaKind {
  const type = mimeType.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  if (
    type.startsWith('text/') ||
    /(word|excel|spreadsheet|csv|powerpoint|presentation|opendocument|rtf)/.test(type)
  ) {
    return 'document';
  }
  return 'other';
}

/** Soft tile + icon colours for a MIME type */
export const mediaTone = (mimeType: string = ''): Tone => MEDIA_TONE[mediaKindOf(mimeType)];

/** Short uppercase extension label, e.g. "PDF" */
export function mediaExtension(name: string = '', mimeType: string = ''): string {
  const fromName = name.includes('.') ? name.split('.').pop() : '';
  return (fromName || mimeType.split('/')[1] || '').slice(0, 5).toUpperCase();
}

export const MediaTypeIcon: React.FC<{ mimeType: string; className?: string }> = ({
  mimeType,
  className = 'h-4 w-4 text-neutral-400',
}) => {
  const Icon = mediaTypeIcon(mimeType);
  return <Icon className={className} strokeWidth={1.75} aria-hidden="true" />;
};

/**
 * Tinted placeholder for a file without an image preview: fills its container with the
 * media-type colour, the type icon and (unless `compact`) the extension.
 */
export const MediaTypeTile: React.FC<{ mimeType: string; name?: string; compact?: boolean; className?: string }> = ({
  mimeType,
  name = '',
  compact = false,
  className = '',
}) => {
  const tone = mediaTone(mimeType);
  return (
    <span className={`flex h-full w-full flex-col items-center justify-center gap-1.5 ${tone.soft} ${className}`}>
      <MediaTypeIcon mimeType={mimeType} className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} ${tone.text}`} />
      {!compact && (
        <span className="text-[10px] font-medium tracking-wide opacity-80">{mediaExtension(name, mimeType)}</span>
      )}
    </span>
  );
};
